import { useRef, useState } from "react";

import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import { BaseInput, Select } from "@smart/itops-sb-design-system-dom";
import { partition } from "@smart/itops-utils-basic";

import {
  DynamicOptionsPlaceholder,
  shouldShowDynamicOptionsPlaceholder,
} from "./dynamic-options-placeholder";
import { FieldGroup } from "./field-group";
import { FieldSet } from "./input";
import { optionsWithFallback } from "./item";
import { Label } from "./label";
import { FieldProps, FieldWrapper } from "./wrapper";
import { fieldName } from "../../hooks";

export const MultiListField = ({
  field,
  index,
  value,
  error,
  loading,
  disabled,
  onChange,
  onBlur,
  isPreview,
}: FieldProps<HTMLInputElement, string[]>) => {
  const { dynamicOptionList } = useFeatureFlags();
  const otherRef = useRef<HTMLInputElement>(null);
  const fieldOptionsWithFallback = optionsWithFallback(field.options);
  const otherOption = { label: "Other", value: "__other__" };
  const options = field.allowCustomResponse
    ? [...fieldOptionsWithFallback, otherOption]
    : fieldOptionsWithFallback;
  const [selected, otherValues] = partition(
    Array.isArray(value) ? value : [value],
    (s) => !!fieldOptionsWithFallback.find((o) => o.value === s),
  );
  // should have only one other value.
  const otherValue = otherValues[0];
  const [other, setOther] = useState(() => !!otherValue);
  const [selectedValues, setSelectedValues] = useState<string[]>(
    otherValue ? [...selected, otherOption.value] : selected,
  );

  const id = fieldName({ field, index });
  const errorId = fieldName({ field, index, suffix: "error" });

  const getValidValues = (values: string[]) =>
    values.filter((v) => !!fieldOptionsWithFallback.find((o) => o.value === v));

  const handleSelectChange = (selectedOptions: string[]) => {
    if (selectedOptions.includes(otherOption.value)) {
      setOther(true);
      setSelectedValues(selectedOptions);
      onChange(
        [
          ...selectedOptions.filter((s) => s !== otherOption.value),
          otherValue,
        ].filter(Boolean),
      );
      otherRef.current?.focus();
    } else {
      const validSelected = getValidValues(selectedOptions);
      setOther(false);
      setSelectedValues(validSelected);
      onChange(validSelected);
    }
    onBlur();
  };

  if (dynamicOptionList && shouldShowDynamicOptionsPlaceholder(field)) {
    return (
      <DynamicOptionsPlaceholder
        field={field}
        index={index}
        error={error}
        loading={loading}
        disabled={disabled}
        isPreview={isPreview}
      />
    );
  }

  return (
    <FieldWrapper disabled={disabled} isLoading={loading}>
      <FieldGroup
        id={id}
        hint={field.hint ?? undefined}
        label={<Label {...field} />}
      >
        <FieldSet
          role="checkbox"
          uri={field.uri}
          error={error}
          errorId={errorId}
        >
          <Select
            id={id}
            value={selectedValues}
            onChange={handleSelectChange}
            options={options}
            disabled={disabled}
            error={!!error}
            aria-errormessage={error && errorId}
            dataTestId="multi-list-field-select"
            isMultiple
          />
          {field.allowCustomResponse && other && (
            <BaseInput
              ref={otherRef}
              name={fieldName({ field, index, suffix: "other-input" })}
              type="text"
              autoFocus
              value={otherValue ?? ""}
              aria-hidden={!other}
              aria-label="Other Value"
              disabled={disabled}
              onChange={(e) => {
                onChange([...getValidValues(selected), e.currentTarget.value]);
              }}
              onBlur={onBlur}
            />
          )}
        </FieldSet>
      </FieldGroup>
    </FieldWrapper>
  );
};
